import React from 'react';
import { ComplianceMatrixEntry, ComplianceMatrixStatus, MatrixImplementationStatus } from './types';
import { TablePagination, useTablePagination } from '../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

interface Props {
  entries: ComplianceMatrixEntry[];
  onSelectEntry: (entry: ComplianceMatrixEntry) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  departmentFilter: string;
  onDepartmentFilterChange: (value: string) => void;
  onCreateEntry: () => void;
}

export const ComplianceMatrixTable: React.FC<Props> = ({
  entries,
  onSelectEntry,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  departmentFilter,
  onDepartmentFilterChange,
  onCreateEntry,
}) => {
  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(entries);
  const matrixPage = useTablePagination(sortedItems, 25);

  const getStatusBadge = (status: ComplianceMatrixStatus) => {
    switch (status) {
      case 'COMPLIANT':
        return <span className="badge-pill badge-success">Compliant</span>;
      case 'PARTIALLY_COMPLIANT':
        return <span className="badge-pill badge-warning">Partially Compliant</span>;
      case 'NON_COMPLIANT':
        return <span className="badge-pill badge-danger">Non-Compliant</span>;
      case 'EXEMPTED':
        return <span className="badge-pill badge-neutral">Exempted</span>;
      case 'PENDING_ASSESSMENT':
      default:
        return <span className="badge-pill badge-info">Pending Assessment</span>;
    }
  };

  const getImplBadge = (status: MatrixImplementationStatus) => {
    switch (status) {
      case 'IMPLEMENTED':
        return <span className="badge-pill badge-success">Implemented</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-warning">In Progress</span>;
      case 'UNDER_REVIEW':
        return <span className="badge-pill badge-info">Under Review</span>;
      case 'EXEMPTED':
        return <span className="badge-pill badge-neutral">Exempted</span>;
      case 'NOT_STARTED':
      default:
        return <span className="badge-pill badge-neutral">Not Started</span>;
    }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Filters Bar */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', flex: 1 }}>
          <input
            type="text"
            placeholder="Search code, summary, or department..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 12px', background: '#ffffff', width: '260px' }}
          />

          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff' }}
          >
            <option value="">All Compliance Statuses</option>
            <option value="COMPLIANT">Compliant</option>
            <option value="PARTIALLY_COMPLIANT">Partially Compliant</option>
            <option value="NON_COMPLIANT">Non-Compliant</option>
            <option value="PENDING_ASSESSMENT">Pending Assessment</option>
            <option value="EXEMPTED">Exempted</option>
          </select>

          <select
            value={departmentFilter}
            onChange={(e) => onDepartmentFilterChange(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff' }}
          >
            <option value="">All Departments</option>
            <option value="Flight Operations">Flight Operations</option>
            <option value="Safety & SMS">Safety & SMS</option>
            <option value="Quality & Compliance">Quality & Compliance</option>
            <option value="Ground Operations">Ground Operations</option>
          </select>
        </div>

        <button
          onClick={onCreateEntry}
          className="btn-primary"
          style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span>+ New Matrix Entry</span>
        </button>
      </div>

      {/* Table */}
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="matrix_code" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Matrix Code
              </SortableTh>
              <SortableTh field="requirement_title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Requirement & Regulation
              </SortableTh>
              <SortableTh field="implementation_status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Implementation
              </SortableTh>
              <SortableTh field="compliance_status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Compliance Status
              </SortableTh>
              <SortableTh field="mapping_coverage_score" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Mapping & Evidence
              </SortableTh>
              <SortableTh field="responsible_department" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Department & Lead
              </SortableTh>
              <SortableTh field="has_active_deviation" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Alerts
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                  No matrix entries found.
                </td>
              </tr>
            ) : (
              matrixPage.paginatedItems.map((entry) => (
                <tr
                  key={entry.id}
                  onClick={() => onSelectEntry(entry)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontWeight: 600, color: '#0284c7', fontFamily: 'monospace' }}>
                    {entry.matrix_code}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{entry.requirement_identifier || 'REQ'} - {entry.requirement_title || 'Requirement'}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{entry.regulation_title || 'Regulation'}</div>
                  </td>
                  <td>
                    {getImplBadge(entry.implementation_status)}
                  </td>
                  <td>
                    {getStatusBadge(entry.compliance_status)}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    <div>Mapping: <span style={{ fontWeight: 600, color: '#0f172a' }}>{entry.mapping_coverage_score}%</span> ({entry.mappings_count || 0})</div>
                    <div>Evidence: <span style={{ fontWeight: 600, color: '#0f172a' }}>{entry.evidence_coverage_score}%</span> ({entry.evidences_count || 0})</div>
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>{entry.responsible_department}</div>
                    <div style={{ color: '#64748b' }}>{entry.lead_owner_name}</div>
                  </td>
                  <td>
                    {entry.is_reassessment_required ? (
                      <span className="badge-pill badge-warning">
                        ⚠️ Reassessment
                      </span>
                    ) : entry.is_overdue_assessment ? (
                      <span className="badge-pill badge-danger">
                        ⏱ Overdue
                      </span>
                    ) : (
                      <span className="badge-pill badge-success">✓ Current</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectEntry(entry);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                    >
                      Detail →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={matrixPage.currentPage}
        totalPages={matrixPage.totalPages}
        pageSize={matrixPage.pageSize}
        totalItems={matrixPage.totalItems}
        onPageChange={matrixPage.setPage}
        onPageSizeChange={matrixPage.setPageSize}
        itemLabel="matrix entries"
      />
    </div>
  );
};
